import {FC, useMemo} from 'react';
import dayjs, {Dayjs} from 'dayjs';
import {
  useGetDailyRevenueComparisonQuery,
  useGetDashboardSummaryQuery,
} from 'store/slices/dashboard';
import {CircularProgress} from '@mui/material';
import {MAX_RANGE_YEARS, isRangeTooLong} from './utils';

import DashboardFilters from './components/DashboardFilters';
import {IGetBranchesListItem} from 'store/models/branches';
import KpiCards from './components/KpiCards';
import PaymentMethodsChart from './components/PaymentMethodsChart';
import RevenueComparisonChart from './components/RevenueComparisonChart';
import {StationsListItem} from 'store/models/stations';
import styles from './Dashboard.module.scss';
import {useAppSelector} from 'store/store';
import {useGetBranchesListQuery} from 'store/slices/branches';
import {useGetStationsListQuery} from 'store/slices/stations';
import {useLocalStorage} from 'hooks';

const Dashboard: FC = () => {
  // filters are persisted in localStorage so they survive a page reload
  const [startIso, setStartIso] = useLocalStorage<string | null>(
    'dashboardStartDate',
    dayjs().subtract(6, 'day').startOf('day').toISOString(),
  );
  const [endIso, setEndIso] = useLocalStorage<string | null>(
    'dashboardEndDate',
    dayjs().endOf('day').toISOString(),
  );
  const [selectedBranches, setSelectedBranches] = useLocalStorage<
    IGetBranchesListItem[]
  >('dashboardBranches', []);
  const [selectedStations, setSelectedStations] = useLocalStorage<
    StationsListItem[]
  >('dashboardStations', []);

  const startDate = useMemo(
    () => (startIso ? dayjs(startIso) : null),
    [startIso],
  );
  const endDate = useMemo(() => (endIso ? dayjs(endIso) : null), [endIso]);

  const userId = useAppSelector((state) => state.auth.id);

  const branchIds = useMemo(
    () =>
      (selectedBranches as IGetBranchesListItem[]).map((branch) => branch.id),
    [selectedBranches],
  );
  const stationIds = useMemo(
    () =>
      (selectedStations as StationsListItem[]).map((station) => station.id),
    [selectedStations],
  );

  // Диапазон шире года не запрашиваем вовсе — иначе бэкенд ложится (#70).
  const rangeTooLong = isRangeTooLong(startDate, endDate);
  const hasRange = Boolean(startDate && endDate) && !rangeTooLong;

  const {data: summary, isFetching: isSummaryFetching} =
    useGetDashboardSummaryQuery(
      {
        startDate: startDate ? startDate.startOf('day').toISOString() : '',
        endDate: endDate ? endDate.endOf('day').toISOString() : '',
        branchIds,
        stationIds,
        iserId: userId,
      },
      {skip: !userId || !hasRange},
    );

  const {data: dailyComparison, isFetching: isComparisonFetching} =
    useGetDailyRevenueComparisonQuery(
      {
        startDate: startDate ? startDate.format('YYYY-MM-DD') : '',
        endDate: endDate ? endDate.format('YYYY-MM-DD') : '',
        branchIds,
        stationIds,
      },
      {skip: !hasRange},
    );

  const isLoadingData =
    hasRange && Boolean(userId) && (isSummaryFetching || isComparisonFetching);

  const {data: branchesData} = useGetBranchesListQuery({});
  const {data: stationsData} = useGetStationsListQuery({
    branches: selectedBranches,
  });

  // While the user edits the masked DD.MM.YYYY input, the DatePicker emits an
  // *invalid* Dayjs (partial date). Calling .toISOString() on it throws
  // "Invalid time value" and crashes the whole dashboard (error boundary).
  // Ignore invalid values; only persist a cleared (null) or fully valid date.
  const handleStartDateChange = (date: Dayjs | null) => {
    if (date && !date.isValid()) return;
    setStartIso(date ? date.toISOString() : null);
  };
  const handleEndDateChange = (date: Dayjs | null) => {
    if (date && !date.isValid()) return;
    setEndIso(date ? date.toISOString() : null);
  };

  const handlePresetSelect = (range: {start: Dayjs; end: Dayjs}) => {
    setStartIso(range.start.toISOString());
    setEndIso(range.end.toISOString());
  };

  const handleBranchesChange = (branches: IGetBranchesListItem[]) => {
    setSelectedBranches(branches);
    // stations depend on the chosen branches, drop stale selections
    setSelectedStations([]);
  };

  const showingLabel = rangeTooLong
    ? `Maximum range is ${MAX_RANGE_YEARS} year`
    : hasRange
    ? `Showing: ${startDate?.format('DD MMM YYYY')} – ${endDate?.format(
        'DD MMM YYYY',
      )}`
    : 'Select a date range';

  return (
    <div className={styles.dashboard}>
      <div className={styles.header}>
        <h1 className={styles.title}>Dashboard Overview</h1>
        <span className={styles.subtitle}>
          Revenue insights for your assigned stations
        </span>
      </div>

      <DashboardFilters
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={handleStartDateChange}
        onEndDateChange={handleEndDateChange}
        branchOptions={branchesData?.data ?? []}
        selectedBranches={selectedBranches}
        onBranchesChange={handleBranchesChange}
        stationOptions={stationsData ?? []}
        selectedStations={selectedStations}
        onStationsChange={setSelectedStations}
        showingLabel={showingLabel}
        onPresetSelect={handlePresetSelect}
        maxRangeYears={MAX_RANGE_YEARS}
      />

      {rangeTooLong ? (
        <div className={styles.rangeWarning}>
          The selected period is longer than {MAX_RANGE_YEARS} year. Please pick
          a shorter range to load the data.
        </div>
      ) : null}

      <div className={styles.contentArea}>
        {isLoadingData ? (
          <div className={styles.loadingOverlay}>
            <CircularProgress size={44} sx={{color: 'var(--primary)'}} />
            <span>Loading data…</span>
          </div>
        ) : null}

        <KpiCards
          summary={summary}
          isLoading={isSummaryFetching || !userId || !hasRange}
        />

        <div className={styles.chartsRow}>
          <PaymentMethodsChart breakdown={summary?.paymentMethodBreakdown} />
          <RevenueComparisonChart
            data={dailyComparison}
            startDate={startDate}
            endDate={endDate}
          />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
